import React from 'react';
import LegalLayout, { LegalSection } from '../components/LegalLayout';

export default function PolitiqueCookies() {
  return (
    <LegalLayout title="Politique de cookies" lastUpdated="1er janvier 2025">
      <p className="text-[#a0a0ab]">
        Cette politique explique comment Kurso utilise les cookies et technologies similaires sur sa plateforme, conformément au RGPD et aux recommandations de la CNIL.
      </p>

      <LegalSection title="1. Qu'est-ce qu'un cookie ?">
        <p>
          Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, smartphone) lors de votre visite sur Kurso. Il permet à notre plateforme de mémoriser vos préférences et d'améliorer votre expérience.
        </p>
        <p className="mt-2">
          Les cookies ont une durée de vie limitée et peuvent être supprimés depuis les paramètres de votre navigateur.
        </p>
      </LegalSection>

      <LegalSection title="2. Cookies utilisés par Kurso">
        <div className="space-y-3">
          <div className="border border-white/10 rounded-xl overflow-hidden">
            <div className="bg-[#ff6b6b]/10 px-3 py-2 flex items-center justify-between">
              <p className="text-white text-xs font-semibold">Cookies strictement nécessaires</p>
              <span className="text-xs bg-[#ff6b6b]/20 text-[#ff6b6b] px-2 py-0.5 rounded-full">Toujours actifs</span>
            </div>
            <div className="p-3 space-y-2">
              <div className="text-xs">
                <div className="flex justify-between text-[#e8e8e8] mb-1">
                  <span className="font-medium">kurso_auth_token</span>
                  <span className="text-[#a0a0ab]">Session</span>
                </div>
                <p className="text-[#a0a0ab]">Jeton d'authentification JWT. Indispensable pour maintenir votre session de connexion.</p>
              </div>
              <div className="text-xs">
                <div className="flex justify-between text-[#e8e8e8] mb-1">
                  <span className="font-medium">kurso_cookie_consent</span>
                  <span className="text-[#a0a0ab]">13 mois</span>
                </div>
                <p className="text-[#a0a0ab]">Mémorise vos préférences de consentement aux cookies.</p>
              </div>
              <div className="text-xs">
                <div className="flex justify-between text-[#e8e8e8] mb-1">
                  <span className="font-medium">kurso_lang</span>
                  <span className="text-[#a0a0ab]">Permanent</span>
                </div>
                <p className="text-[#a0a0ab]">Mémorise votre préférence de langue (FR/EN).</p>
              </div>
            </div>
          </div>

          <div className="border border-white/10 rounded-xl overflow-hidden">
            <div className="bg-[#ffd93d]/10 px-3 py-2 flex items-center justify-between">
              <p className="text-white text-xs font-semibold">Cookies analytiques</p>
              <span className="text-xs bg-[#ffd93d]/20 text-[#ffd93d] px-2 py-0.5 rounded-full">Sur consentement</span>
            </div>
            <div className="p-3 space-y-2">
              <div className="text-xs">
                <div className="flex justify-between text-[#e8e8e8] mb-1">
                  <span className="font-medium">Mixpanel</span>
                  <span className="text-[#a0a0ab]">1 an</span>
                </div>
                <p className="text-[#a0a0ab]">Mesure d'audience anonymisée : pages visitées, fonctionnalités utilisées, durée des sessions. Ces données nous permettent d'améliorer la plateforme. Aucune donnée personnelle identifiable n'est transmise.</p>
              </div>
            </div>
          </div>

          <div className="border border-white/10 rounded-xl overflow-hidden">
            <div className="bg-white/5 px-3 py-2 flex items-center justify-between">
              <p className="text-white text-xs font-semibold">Cookies de paiement</p>
              <span className="text-xs bg-white/10 text-[#a0a0ab] px-2 py-0.5 rounded-full">Tiers (Stripe)</span>
            </div>
            <div className="p-3 text-xs">
              <p className="text-[#a0a0ab]">Stripe dépose des cookies lors du processus de paiement pour sécuriser la transaction et prévenir la fraude. Ces cookies sont soumis à la politique de confidentialité de Stripe (stripe.com/privacy).</p>
            </div>
          </div>
        </div>
      </LegalSection>

      <LegalSection title="3. Gestion de vos cookies">
        <p>Vous pouvez gérer vos préférences de cookies de plusieurs façons :</p>
        <ul className="list-disc pl-5 space-y-2 text-[#a0a0ab] mt-2">
          <li>
            <strong className="text-[#e8e8e8]">Bannière Kurso :</strong> Lors de votre première visite, notre bannière vous permet d'accepter, refuser ou personnaliser les cookies non-essentiels.
          </li>
          <li>
            <strong className="text-[#e8e8e8]">Paramètres du navigateur :</strong> Vous pouvez configurer votre navigateur pour bloquer tous les cookies ou être notifié lors de leur dépôt.
            <ul className="list-none pl-4 mt-1 space-y-0.5 text-xs">
              <li>Chrome : Paramètres → Confidentialité → Cookies</li>
              <li>Firefox : Options → Vie privée et sécurité</li>
              <li>Safari : Préférences → Confidentialité</li>
              <li>Edge : Paramètres → Cookies et autorisations de site</li>
            </ul>
          </li>
          <li>
            <strong className="text-[#e8e8e8]">Opt-out Mixpanel :</strong>{' '}
            <a href="https://mixpanel.com/optout" target="_blank" rel="noopener noreferrer" className="text-[#ff6b6b] hover:underline">
              mixpanel.com/optout
            </a>
          </li>
        </ul>
        <p className="mt-3 bg-[#ff6b6b]/5 border border-[#ff6b6b]/20 rounded-xl p-3 text-xs text-[#a0a0ab]">
          <strong className="text-white">Attention :</strong> La désactivation des cookies strictement nécessaires peut empêcher le fonctionnement correct de Kurso (connexion impossible, perte de préférences).
        </p>
      </LegalSection>

      <LegalSection title="4. Durée de conservation">
        <p>
          Les cookies analytiques sont conservés pour une durée maximale de <strong>13 mois</strong>, conformément aux recommandations de la CNIL. Votre consentement est redemandé passé ce délai.
        </p>
      </LegalSection>

      <LegalSection title="5. Modifications de la politique">
        <p>
          Kurso peut mettre à jour cette politique pour refléter des changements dans nos pratiques ou pour des raisons légales. La date de dernière mise à jour est indiquée en haut de cette page.
        </p>
      </LegalSection>

      <LegalSection title="6. Contact">
        <p>
          Pour toute question relative à cette politique cookies :{' '}
          <a href="mailto:dpo@mykurso.fr" className="text-[#ff6b6b] hover:underline">dpo@mykurso.fr</a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
